import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTwitterCampaigns } from '../api';
import type { TwitterCampaignEntry } from '../api';

const EMPTY: string[] = [];

/**
 * Backend errors arrive as `error.cause` = response body: `{ message }` for X API
 * failures (502), `{ detail }` for DRF errors such as a flow missing in the DB (404),
 * `{ message, details }` for validation errors (400).
 */
export const getTwitterApiErrorMessage = (error: unknown, fallback: string): string => {
  const cause = (error as { cause?: Record<string, unknown> } | null)?.cause;
  for (const key of ['details', 'detail', 'message'] as const) {
    const value = cause?.[key];
    if (typeof value === 'string' && value) return value;
  }
  const message = (error as { message?: unknown } | null)?.message;
  return typeof message === 'string' && message ? message : fallback;
};

const useTwitterCampaignsData = () => {
  useFormContext();
  const accountId = (useWatch({ name: 'accountId' }) as string | undefined) ?? '';
  const selectedCampaigns = (useWatch({ name: 'selectedCampaigns' }) as string[] | undefined) ?? EMPTY;
  const selectedOnly = (useWatch({ name: 'selectedCampaignsOnly' }) as boolean | undefined) ?? false;
  const filterByName = (useWatch({ name: 'filterCampaignsByName' }) as string[] | undefined) ?? EMPTY;

  const { data, isLoading, isError, error } = useTwitterCampaigns({ params: { accountId } });

  // Selected campaigns stay listed even when the name filter would hide them.
  const gridRows = useMemo<TwitterCampaignEntry[]>(() => {
    if (!data?.length) return [];

    const selected = new Set(selectedCampaigns);
    const matchesName = (row: TwitterCampaignEntry) => {
      if (!filterByName.length) return true;
      const name = row.campaign_name.toLowerCase();
      return filterByName.every((chip) => name.includes(chip.toLowerCase()));
    };

    return data
      .filter((row) => selected.has(row.campaign_id) || matchesName(row))
      .filter((row) => !selectedOnly || selected.has(row.campaign_id));
  }, [data, selectedCampaigns, selectedOnly, filterByName]);

  return {
    gridRows,
    totalCount: data?.length ?? 0,
    isLoading,
    isError,
    errorMessage: isError ? getTwitterApiErrorMessage(error, 'Failed to load campaigns') : undefined,
  };
};

export default useTwitterCampaignsData;
